import {
  Center,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  IconButton,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { LucideUsers, ArrowRightFromLine, Archive } from 'lucide-react';
import { Suspense, useCallback, useMemo } from 'react';
import { Trans, Plural } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import pickBy from 'lodash-es/pickBy.js';

import { CenteredSpinner } from '@duke-hq/app-provider-web/components/Loading';
import { constructQueryParams, constructURL } from '@duke-hq/lib';
import type { MenuItemsType } from '@duke-hq/ui/components/SubSidebar/MenuItems';
import { SidebarLayout } from '@duke-hq/ui/components/SubSidebar/SidebarLayout';
import { StudentDetailPage } from './StudentDetailPage';
import { StudentList } from './StudentList';
import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { useTenantContext } from '../TenantContext';
import { CourseReferralButton } from '../components/CourseReferralModal';
import QuickReferralModal from '../components/QuickReferralModal';
import { ParticipantFilters } from './ParticipantFilters';

import { emptyPaginationResponse, SortOrder, parseSortOrder } from '@duke-hq/lib/pagination';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';

import type { StudentsURLParams } from './types';

function generateMenuItems(
  filters: StudentsURLParams,
  studentsSnapshot?: RouterOutputs['users']['snapshot'],
): MenuItemsType[] {
  const baseUrl = '/students';
  const { page, ...existingParams } = filters;

  const {
    all_count = 0,
    active_count = 0,
    inactive_count = 0,
    archive_count = 0,
  } = studentsSnapshot ?? {};

  return [
    {
      label: t`All`,
      link: `${baseUrl}${constructQueryParams({ ...existingParams, status: undefined })}`,
      icon: LucideUsers,
      snapshot: all_count,
      criteria: ['status'],
    },
    {
      label: t`Active`,
      link: `${baseUrl}${constructQueryParams({ ...existingParams, status: 'ACTIVE' })}`,
      icon: LucideUsers,
      snapshot: active_count,
      criteria: ['status'],
    },
    {
      label: t`Inactive`,
      link: `${baseUrl}${constructQueryParams({ ...existingParams, status: 'INACTIVE' })}`,
      icon: LucideUsers,
      snapshot: inactive_count,
      criteria: ['status'],
    },
    {
      label: t`Archive`,
      link: `${baseUrl}${constructQueryParams({ ...existingParams, status: 'ARCHIVE' })}`,
      icon: Archive,
      snapshot: archive_count,
      criteria: ['status'],
    },
  ];
}

export function StudentsPage() {
  const tenantCtx = useTenantContext();
  const isTenantUsingNewReferralProcess = tenantCtx?.settings.uses_new_referral_process;
  const canViewConsultantDetails = tenantCtx?.permissions.includes(
    'canCreateReferralsForTeamMembers',
  );
  const { studentId } = useParams<{ studentId: string }>();
  const { pageSize, currentPage, setPage } = usePagination();
  const [searchParams, setSearchParams] = useSearchParams({
    orderBy: ['last_authenticated_at'],
    sort: [SortOrder.DESC],
  });
  const navigate = useNavigate();
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();

  const querySearch = searchParams.get('search') ?? '';

  const URLFilters = useMemo(() => {
    const urlFilters = Object.fromEntries(searchParams.entries());
    const { page, status, consultantIds, orderBy, sort, ...rest } = urlFilters;

    return {
      ...rest,
      orderBy: orderBy ?? 'last_authenticated_at',
      sort: parseSortOrder(sort) ?? SortOrder.DESC,
      consultantIds: consultantIds ?? undefined,
      status: status ?? undefined,
      page: page ?? '1',
    };
  }, [searchParams]);

  const { data: students, isFetching } = trpcTenant.users.listStudentsWithPagination.useQuery(
    {
      search: querySearch.trim(),
      excludeInternalEnrolments: true,
      filters: URLFilters,
      pagination: {
        page: Number(currentPage),
        limit: pageSize,
      },
    },
    {
      initialData: emptyPaginationResponse(),
      placeholderData: (previousData) => previousData,
    },
  );

  const { data: snapshot } = trpcTenant.users.snapshot.useQuery({
    excludeInternalEnrolments: true,
  });

  const { data: consultants } = trpcTenant.users.allUsersAtAvailableSiteLocations.useQuery();
  const sideDrawerItems = generateMenuItems(URLFilters, snapshot);

  const consultantOptions = useMemo(() => {
    const consultantsToDisplay: {
      id: string;
      first_name: string;
      last_name: string | null;
    }[] = consultants || [];

    return consultantsToDisplay.map((consultant) => ({
      id: consultant.id,
      value: consultant.id,
      label: `${consultant.first_name} ${consultant.last_name}`,
    }));
  }, [consultants]);

  const { isOpen } = useDisclosure({
    isOpen: !(studentId == null),
  });

  const handleFilterChange = useCallback(
    (newFilters: Partial<StudentsURLParams>) => {
      const updatedFilters = pickBy(
        { ...Object.fromEntries(searchParams.entries()), ...newFilters },
        (value) => value != null && value != '',
      );

      if (updatedFilters.page != null && updatedFilters.page === '1') {
        delete updatedFilters.page;
      }

      const newSearchParams = new URLSearchParams(updatedFilters as Record<string, string>);
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  const onCloseHandler = useCallback(() => {
    const url = constructURL(`/students`, URLFilters);
    navigate(url);
  }, [navigate, URLFilters]);

  return (
    <>
      {studentId != null ? (
        <Drawer isOpen={isOpen} onClose={onCloseHandler} size="xl">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton onClick={onCloseHandler} />
            <Suspense fallback={<CenteredSpinner />}>
              <StudentDetailPage />
            </Suspense>
          </DrawerContent>
        </Drawer>
      ) : null}

      <SidebarLayout items={sideDrawerItems} label={t`Participants`}>
        <TableViewLayout
          pageHeader={[
            !isExpanded && (
              <IconButton
                key="expand"
                aria-label="expand secondary sidebar"
                icon={<ArrowRightFromLine />}
                variant="tertiary"
                onClick={toggleExpandSubSidebar}
                size="md"
              />
            ),
            <PageHeading key="page-heading">
              {URLFilters.status === 'ACTIVE' ? (
                <Trans>Active Participants</Trans>
              ) : URLFilters.status === 'INACTIVE' ? (
                <Trans>Inactive Participants</Trans>
              ) : URLFilters.status === 'ARCHIVE' ? (
                <Trans>Archived Participants</Trans>
              ) : (
                <Trans>All Participants</Trans>
              )}
            </PageHeading>,
          ]}
          pageActions={
            isTenantUsingNewReferralProcess ? <CourseReferralButton /> : <QuickReferralModal />
          }
          filters={
            <>
              {canViewConsultantDetails === true ? (
                <ParticipantFilters
                  filters={URLFilters}
                  consultantOptions={consultantOptions}
                  onChange={handleFilterChange}
                />
              ) : null}
              <SearchInput maxW="search-input" placeholder={t`Search by name or email`} />
            </>
          }
          tableActionsLeft={
            <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
              <Plural
                value={students.pagination.totalCount || 0}
                zero="0 participants"
                one="1 participant"
                other="# participants"
              />
            </Text>
          }
          table={
            isFetching ? (
              <Center p={8}>
                <Spinner size="xl" transitionDelay={'2900ms'} />
              </Center>
            ) : students.items.length === 0 ? (
              <Center p={8}>
                <Text textAlign="center">
                  <Trans>No students found</Trans>
                </Text>
              </Center>
            ) : (
              <StudentList
                students={students.items}
                urlParams={URLFilters}
                onFilters={handleFilterChange}
              />
            )
          }
          pagination={
            Array.isArray(students.items) && students.items.length > 0 ? (
              <Pagination pagination={students.pagination} onPageChange={setPage} />
            ) : null
          }
        />
      </SidebarLayout>
    </>
  );
}
